import { memo, useMemo, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import SelectMenu from "@hl/base-components/lib/SelectMenu";
import {
  LockThin,
  PlusCircle,
} from "@hl/base-components/lib/assets/icons.generated";
import { ColorSchemeMode } from "@hl/base-components/lib/theme/ColorSchemeContext";
import {
  OUTLINE_COLOR,
  PRIMARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import usePageVisitedEvent from "@hl/shared-features/lib/hooks/usePageVisitedEvent";
import { Box, Button, Divider, Group, useMantineTheme } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { Link } from "react-router-dom";

import { TOOLS_URL } from "config";
import CreateFirstV2 from "~/features/dashboard/CreateFirstV2";
import DashboardContent from "~/features/dashboard/DashboardContent";

import CustomPaginationWrapper from "../tokens/components/CustomPaginationWrapper";

import GateCard from "./GateCard";
import { useGatesQuery } from "./queries/gates.graphql.generated";

enum GateSortableField {
  CREATED_DATE = "CREATED_DATE",
  UPDATED_DATE = "UPDATED_DATE",
  NAME = "NAME",
}

enum GateSortDirection {
  ASC = "ASC",
  DESC = "DESC",
}

interface SortConfig {
  name: string;
  field: GateSortableField;
  direction: GateSortDirection;
}

const SORT_CONFIG: Record<string, SortConfig> = {
  NEWEST: {
    name: "Newest",
    field: GateSortableField.CREATED_DATE,
    direction: GateSortDirection.DESC,
  },
  OLDEST: {
    name: "Oldest",
    field: GateSortableField.CREATED_DATE,
    direction: GateSortDirection.ASC,
  },
  ALPHABETICAL: {
    name: "Alphabetical",
    field: GateSortableField.NAME,
    direction: GateSortDirection.ASC,
  },
  UPDATED: {
    name: "Recently updated",
    field: GateSortableField.UPDATED_DATE,
    direction: GateSortDirection.DESC,
  },
};

const GatesDashboardV2 = () => {
  const theme = useMantineTheme();
  const [activeSort, setActiveSort] = useLocalStorage<SortConfig>({
    key: "gates-sorting",
    defaultValue: SORT_CONFIG.NEWEST,
  });

  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(1);
  const { data, loading } = useGatesQuery({
    fetchPolicy: "cache-and-network",
  });

  usePageVisitedEvent("GATES");

  const gates = useMemo(() => {
    return [...(data?.gates ?? [])].sort((a, b) => {
      if (activeSort.field === GateSortableField.UPDATED_DATE) return 0;
      if (activeSort.field === GateSortableField.NAME) {
        return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
      }
      if (activeSort.field === GateSortableField.CREATED_DATE) {
        const dirFlag =
          activeSort.direction === GateSortDirection.DESC ? 1 : -1;
        return dirFlag * ((a.createdAt ?? 0) < (b.createdAt ?? 0) ? 1 : -1);
      }

      return 0;
    });
  }, [data?.gates, activeSort]);

  return (
    <DashboardContent
      header={
        <Group>
          <Button
            component={Link}
            color={
              theme.colorScheme === ColorSchemeMode.LIGHT
                ? PRIMARY_COLOR
                : OUTLINE_COLOR
            }
            to={TOOLS_URL.gates.create}
            leftIcon={<PlusCircle />}
          >
            Create a gate
          </Button>
        </Group>
      }
    >
      <AppSEO title="Gates" />
      <Box mb={48}>
        <SelectMenu
          title="Sort by"
          selectedItem={activeSort.name}
          items={Object.values(SORT_CONFIG).map((config) => ({
            label: config.name,
            onClick: () => {
              setActiveSort(config);
            },
          }))}
          w={210}
        />
      </Box>
      {loading ? (
        <LoadingPage />
      ) : gates.length === 0 ? (
        <CreateFirstV2
          entity="gate"
          redirectUrl={TOOLS_URL.gates.create}
          header={<LockThin width={64} height={64} />}
        />
      ) : (
        <CustomPaginationWrapper
          itemsPerPage={10}
          activePage={activePage}
          setPage={setPage}
          containerStyle={{
            overflow: gates?.length === 1 ? "visible" : "auto",
          }}
          setLowerBound={setLowerBound}
          setUpperBound={setUpperBound}
          maxLength={gates.length}
        >
          {(gates ?? [])
            .slice(lowerBound - 1, upperBound)
            .map((gate, index) => {
              return (
                <Box key={gate.id}>
                  <GateCard
                    key={gate.id}
                    id={gate.id}
                    name={gate.name ?? ""}
                    created={gate.createdAt ?? ""}
                  />
                  {index < upperBound - lowerBound && <Divider />}
                </Box>
              );
            })}
        </CustomPaginationWrapper>
      )}
    </DashboardContent>
  );
};

export default memo(GatesDashboardV2);
